import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import apiAxios from "../../../services/apiAxios";
import moment from "moment";
import DropDown from "../../common/icons/DropDown";
import Receipt from "../../common/icons/Receipt";
import CreateOrder from "../../common/icons/CreateOrder";
import Info from "../../common/icons/Info";

const MonthDropdown = ({ selected, dropdownKey, dropdownRefs, showMonthDropdown, setShowMonthDropdown, availableMonths, toggleMonth }) => (
    <div className="relative" ref={dropdownRefs[dropdownKey]}>
        <button
            onClick={() => setShowMonthDropdown(prev => ({ ...prev, [dropdownKey]: !prev[dropdownKey] }))}
            className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-primary-100 rounded-[10px] text-[13px] font-bold text-secondry-700 hover:border-primary-400 transition-all shadow-sm min-w-[130px]"
        >
            {selected.length > 0 ? `${selected.length} Months` : `${availableMonths.length} Months`}
            <span className={`transition-transform duration-200 ${showMonthDropdown[dropdownKey] ? 'rotate-180' : ''}`}>
                <DropDown width={14} height={14} fill="currentColor" />
            </span>
        </button>
        {showMonthDropdown[dropdownKey] && (
            <div className="absolute right-0 mt-2 w-52 bg-white border border-primary-100 rounded-[14px] shadow-2xl z-[100] max-h-[300px] overflow-y-auto p-2.5">
                {availableMonths.map(month => (
                    <label key={month} className="flex items-center gap-3 px-3 py-2 hover:bg-primary-50 rounded-[10px] cursor-pointer transition-all">
                        <input
                            type="checkbox"
                            checked={selected.includes(month)}
                            onChange={() => toggleMonth(month, dropdownKey)}
                            className="w-4 h-4 accent-primary-600 rounded"
                        />
                        <span className="text-[14px] text-secondry-700 font-semibold">{month}</span>
                    </label>
                ))}
            </div>
        )}
    </div>
);

const StatCard = ({ title, icon: Icon, counts, selectedMonths, hideHistory = false }) => (
    <div className="flex-1 bg-white border border-primary-100 rounded-[20px] p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden group">
        <div className="absolute -right-3 -top-3 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity">
            <Icon width={100} height={100} fill="#2563eb" />
        </div>

        <div className="relative z-10">
            <h3 className="text-[28px] font-extrabold text-secondry-800 leading-none mb-1">
                {selectedMonths.length > 0 ? counts.custom : counts.total}
            </h3>
            <p className="text-[12px] font-bold text-gray-400 mb-4 uppercase tracking-wider">{title}</p>

            {!hideHistory && (
                <div className="grid grid-cols-3 gap-2 mt-2 pt-3 border-t border-gray-100">
                    <div className="text-left">
                        <p className="text-[7.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-0.5">This Month</p>
                        <p className="text-[14px] font-extrabold text-secondry-700">{counts.thisMonth}</p>
                    </div>
                    <div className="text-left">
                        <p className="text-[7.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-0.5">Last Month</p>
                        <p className="text-[14px] font-extrabold text-secondry-700">{counts.lastMonth}</p>
                    </div>
                    <div className="text-left">
                        <p className="text-[7.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-0.5">Prev Month</p>
                        <p className="text-[14px] font-extrabold text-secondry-700">{counts.lastToLastMonth}</p>
                    </div>
                </div>
            )}
        </div>
    </div>
);

const ProductTile = ({ product, type }) => {
    const formatCurrency = (val) => `₹${val.toLocaleString("en-IN")}`;
    const discount = parseFloat(product.pricing?.discount || 0);

    return (
        <div className="min-w-[300px] bg-white border border-primary-100/60 rounded-[20px] p-5 shadow-sm hover:shadow-xl hover:translate-y-[-4px] transition-all duration-400 cursor-default group flex flex-col justify-between">
            <div>
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 bg-gray-50 text-gray-500 text-[9px] font-extrabold uppercase tracking-widest rounded-md border border-gray-100">
                            {product.type}
                        </span>
                        {product.isCustom && (
                            <span className="bg-amber-100 text-amber-800 text-[8px] font-extrabold px-1.5 py-0.5 rounded uppercase border border-amber-200">
                                Custom
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-lg border border-primary-100/50">
                        <span className="text-[13px] font-extrabold leading-none">{product.usageCount}</span>
                        <span className="text-[8.5px] font-extrabold uppercase tracking-tighter opacity-70">
                            {type === "prescribed" ? "Rx" : "Qty"}
                        </span>
                    </div>
                </div>

                <div className="mb-4">
                    <h4 className="text-[16px] font-extrabold text-secondry-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-0.5 leading-tight">
                        {product.productName}
                    </h4>
                    <p className="text-[10px] font-extrabold text-secondry-300 uppercase tracking-widest leading-none mb-3">
                        {product.manufacturer}
                    </p>

                    <div className="pt-3 border-t border-gray-50">
                        <span className="inline-block text-[8.5px] font-extrabold text-primary-400 uppercase tracking-widest mb-1.5 px-1.5 py-0.5 bg-primary-50/50 rounded-md">
                            Composition
                        </span>
                        <p className="text-[12px] text-secondry-600 font-bold line-clamp-2 leading-snug italic min-h-[34px]">
                            {product.drugs || "N/A"}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-2 pt-4 border-t border-gray-100">
                <div className="flex flex-col">
                    <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">MRP</span>
                    <span className="text-[15px] font-extrabold text-secondry-800 leading-none truncate">
                        {formatCurrency(parseFloat(product.pricing?.mrp || 0))}
                    </span>
                </div>

                <div className="flex flex-col items-center">
                    <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">Factor</span>
                    <span className={`text-[15px] font-extrabold leading-none truncate ${product.lookupMarginType ? "text-secondry-800" : "text-gray-300"}`}>
                        {product.lookupMarginType || "N/A"}
                    </span>
                </div>

                <div className="flex flex-col items-center">
                    <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">Discount</span>
                    <span className={`text-[15px] font-extrabold leading-none truncate ${discount > 0 ? "text-secondry-800" : "text-gray-300"}`}>
                        {discount > 0 ? `${discount}%` : "0%"}
                    </span>
                </div>

                <div className="flex flex-col items-end">
                    <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">Stock</span>
                    <span className={`text-[15px] font-extrabold leading-none truncate ${Number(product.stock) > 6 ? "text-success-600" : Number(product.stock) === 0 ? "text-red-500" : "text-yellow-700"}`}>
                        {product.stock || 0}
                    </span>
                </div>
            </div>
        </div>
    );
};

const CustomAnalysisModal = ({ isOpen, onClose, availableMonths, doctorId, idStore, SID }) => {
    const thisMonth = moment().format("MMMM YYYY");
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState([thisMonth]);
    const [analytics, setAnalytics] = useState({});
    const [searchLoading, setSearchLoading] = useState(false);
    const dropdownRef = useRef(null);
    const modalDropdownRef = useRef(null);
    const [showResults, setShowResults] = useState(false);
    const [localShowDropdown, setLocalShowDropdown] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
            setSearchResults([]);
            setSelectedProducts([]);
            setSelectedMonths([thisMonth]);
            setAnalytics({});
        }

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowResults(false);
            }
            if (modalDropdownRef.current && !modalDropdownRef.current.contains(event.target)) {
                setLocalShowDropdown(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, thisMonth]);

    const handleSearch = useCallback(async (term) => {
        if (!term) {
            setSearchResults([]);
            return;
        }
        setSearchLoading(true);
        try {
            const id = doctorId || idStore || SID;
            // The backend getFrequentProductsV1 expects 'search' query parameter
            const response = await apiAxios.get(`api/doctor/get-products-frequent-v1/${id}?search=${term}`);
            setSearchResults(response.data.searchProducts || []);
            setShowResults(true);
        } catch (error) {
            console.error("Search failed:", error);
        } finally {
            setSearchLoading(false);
        }
    }, [doctorId, idStore, SID]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchTerm) handleSearch(searchTerm);
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [searchTerm, handleSearch]);

    const fetchProductData = useCallback(async (name, months) => {
        try {
            const response = await apiAxios.get("api/doctor/get-custom-analytics", {
                params: {
                    productNames: name,
                    months: months.join(",")
                }
            });
            if (response.data.results && response.data.results.length > 0) {
                const result = response.data.results.find(r => r.productName === name) || response.data.results[0];
                setAnalytics(prev => ({ ...prev, [name]: result }));
            }
        } catch (error) {
            console.error("Failed to fetch product analytics:", error);
        }
    }, []);

    const addProduct = async (product) => {
        const name = product.productName || (product.product && product.product.productName);
        if (!name || selectedProducts.includes(name)) return;

        setSelectedProducts(prev => [name, ...prev]);
        setSearchTerm("");
        setShowResults(false);
        await fetchProductData(name, selectedMonths);
    };

    useEffect(() => {
        if (selectedProducts.length > 0) {
            selectedProducts.forEach(name => fetchProductData(name, selectedMonths));
        }
    }, [selectedMonths, selectedProducts, fetchProductData]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 md:p-8 backdrop-blur-xl bg-white/60">
            <div className="w-full h-full max-w-7xl bg-white border border-primary-100 rounded-[40px] shadow-[0_32px_120px_-20px_rgba(0,0,0,0.2)] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
                {/* Modal Header */}
                <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-primary-50/30">
                    <div>
                        <h2 className="text-3xl font-extrabold text-secondry-800 tracking-tight">Custom Analysis</h2>
                        <p className="text-gray-500 font-medium">Investigate specific products with isolated metrics</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-12 h-12 flex items-center justify-center bg-white border border-primary-100 rounded-full text-gray-400 hover:bg-gray-50 hover:text-red-500 transition-all shadow-sm"
                    >
                        <span className="text-2xl font-light leading-none">✕</span>
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-8 scrollbar-hide">
                    {/* Search & Filter Section */}
                    <div className="flex items-center gap-4 max-w-4xl mx-auto mb-12 relative z-[150]">
                        <div className="flex-1 relative" ref={dropdownRef}>
                            <div className="relative group">
                                <input
                                    type="text"
                                    placeholder="Search products to analyze..."
                                    value={searchTerm}
                                    onFocus={() => {
                                        setLocalShowDropdown(false);
                                        if (searchResults.length > 0) setShowResults(true);
                                    }}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full py-5 px-8 bg-gray-50 border-2 border-primary-50 rounded-[28px] text-lg font-semibold text-secondry-800 outline-none focus:border-primary-400 focus:bg-white transition-all shadow-inner"
                                />
                                {searchLoading && (
                                    <div className="absolute right-6 top-1/2 -translate-y-1/2">
                                        <div className="w-6 h-6 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                                    </div>
                                )}
                            </div>

                            {showResults && searchResults.length > 0 && (
                                <div className="absolute top-full left-0 right-0 mt-3 bg-white border border-primary-100 rounded-[28px] shadow-2xl z-[100] max-h-[400px] overflow-y-auto p-2 pr-1.5 thin-scrollbar animate-in fade-in slide-in-from-top-4">
                                    <div className="flex flex-col gap-0.5">
                                        {searchResults.map((result, idx) => {
                                            const p = result.product || result;
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => addProduct(p)}
                                                    className="p-5 hover:bg-primary-50 rounded-[22px] cursor-pointer transition-all border border-transparent hover:border-primary-100 group"
                                                >
                                                    <h4 className="font-bold text-secondry-800 text-lg group-hover:text-primary-600 truncate">{p.productName}</h4>
                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mt-1">{p.type} • {p.manufacturer}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Local Duration Filter */}
                        <div className="relative" ref={modalDropdownRef}>
                            <button
                                onClick={() => {
                                    setLocalShowDropdown(!localShowDropdown);
                                    setShowResults(false);
                                }}
                                className="flex items-center gap-3 px-6 py-[22px] bg-gray-50 border-2 border-primary-50 rounded-[24px] text-[15px] font-bold text-secondry-700 hover:border-primary-400 transition-all shadow-inner min-w-[240px]"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary-500">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                {selectedMonths.length > 0 ? `${selectedMonths.length} Months` : `${availableMonths.length} Months`}
                                <span className={`ml-auto transition-transform duration-200 ${localShowDropdown ? 'rotate-180' : ''}`}>
                                    <DropDown width={14} height={14} fill="currentColor" />
                                </span>
                            </button>
                            {localShowDropdown && (
                                <div className="absolute right-0 mt-3 w-full bg-white border border-primary-100 rounded-[24px] shadow-2xl z-[200] flex flex-col overflow-hidden animate-in fade-in slide-in-from-top-4">
                                    <div className="px-5 py-4 border-b border-gray-50 flex-shrink-0">
                                        <p className="text-[10px] font-bold text-primary-400 uppercase tracking-[0.2em]">Select Window</p>
                                    </div>
                                    <div className="max-h-[300px] overflow-y-auto p-2 pr-1.5 thin-scrollbar">
                                        <div className="flex flex-col gap-0.5">
                                            {availableMonths.map(month => (
                                                <label key={month} className="flex items-center gap-3 px-3 py-2.5 hover:bg-primary-50 rounded-[14px] cursor-pointer transition-all group">
                                                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${selectedMonths.includes(month) ? "bg-primary-600 border-primary-600" : "border-gray-200 group-hover:border-primary-300"}`}>
                                                        {selectedMonths.includes(month) && <span className="text-white text-[10px]">✓</span>}
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedMonths.includes(month)}
                                                            onChange={() => {
                                                                setSelectedMonths(prev =>
                                                                    prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
                                                                );
                                                            }}
                                                            className="hidden"
                                                        />
                                                    </div>
                                                    <span className="text-[14px] text-secondry-700 font-semibold">{month}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Desktop Parallel Grid */}
                    <div className="flex flex-col gap-16">
                        {selectedProducts.map(name => {
                            const stats = analytics[name];
                            return (
                                <div key={name} className="animate-in fade-in slide-in-from-bottom-8 duration-500">
                                    <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-2">
                                        <div>
                                            <h3 className="text-2xl font-bold text-primary-700 uppercase tracking-tight">{name}</h3>
                                        </div>
                                        <button
                                            onClick={() => setSelectedProducts(prev => prev.filter(p => p !== name))}
                                            className="text-[10px] font-bold text-red-500 bg-red-50/50 px-4 py-2 rounded-full hover:bg-red-100 transition-colors uppercase tracking-widest border border-red-100"
                                        >
                                            Remove Product
                                        </button>
                                    </div>

                                    {!stats ? (
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 opacity-40">
                                            <div className="h-44 bg-gray-50 rounded-[28px] animate-pulse border border-dashed border-gray-200 flex items-center justify-center">
                                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Aggregating Pattern...</p>
                                            </div>
                                            <div className="h-44 bg-gray-50 rounded-[28px] animate-pulse border border-dashed border-gray-200 flex items-center justify-center">
                                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Calculating Commercials...</p>
                                            </div>
                                            <div className="h-44 bg-gray-50 rounded-[28px] animate-pulse border border-dashed border-gray-200 flex items-center justify-center">
                                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Fetching Live Stock...</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                                            <div className="flex flex-col gap-4">
                                                <StatCard
                                                    title="Total Prescribed Qty"
                                                    icon={Info}
                                                    counts={stats.prescribed}
                                                    selectedMonths={selectedMonths}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-4">
                                                <StatCard
                                                    title="Total Ordered Qty"
                                                    icon={CreateOrder}
                                                    counts={stats.ordered}
                                                    selectedMonths={selectedMonths}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-4">
                                                <StatCard
                                                    title="Current Stock"
                                                    icon={Receipt}
                                                    counts={{ total: stats.stock, custom: stats.stock }}
                                                    selectedMonths={selectedMonths}
                                                    hideHistory={true}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {selectedProducts.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-32 opacity-20">
                                <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                                    <MagnifyingGlass width={48} height={48} />
                                </div>
                                <h3 className="text-2xl font-extrabold text-gray-900 uppercase tracking-widest">Nothing selected</h3>
                                <p className="mt-2 font-semibold">Add products above to begin isolated analysis</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const MagnifyingGlass = ({ width, height, fill = "currentColor" }) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 21L15.803 15.803M15.803 15.803C17.2096 14.3964 18 12.4886 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18C12.4886 18 14.3964 17.2096 15.803 15.803Z" stroke={fill} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Analytics = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [accountCreatedAt, setAccountCreatedAt] = useState(null);
    const [isCustomAnalysisOpen, setIsCustomAnalysisOpen] = useState(false);

    // Filters
    const thisMonth = moment().format("MMMM YYYY");
    const [selectedMonths, setSelectedMonths] = useState([thisMonth]);
    const [prescribedMonths, setPrescribedMonths] = useState([thisMonth]);
    const [orderedMonths, setOrderedMonths] = useState([thisMonth]);

    const doctorDetails = useSelector(state => state?.doctor?.doctorDetails?.doctor);
    const doctorId = doctorDetails?.DID;
    const idStore = doctorDetails?.SID;
    const SID = useSelector(state => state.store.storeDetail?.store?.SID);

    // Dropdown visibility
    const [showMonthDropdown, setShowMonthDropdown] = useState({
        patient: false,
        prescribed: false,
        ordered: false
    });

    // Refs for outside click
    const patientRef = useRef(null);
    const prescribedRef = useRef(null);
    const orderedRef = useRef(null);

    const dropdownRefs = useMemo(() => ({
        patient: patientRef,
        prescribed: prescribedRef,
        ordered: orderedRef
    }), []);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                months: selectedMonths.join(","),
                prescribedMonths: prescribedMonths.join(","),
                orderedMonths: orderedMonths.join(",")
            };
            const response = await apiAxios.get("api/doctor/get-analytics", { params });
            setData(response.data);
            if (response.data.accountCreatedAt) {
                setAccountCreatedAt(response.data.accountCreatedAt);
            }
        } catch (error) {
            console.error("Failed to fetch analytics:", error);
        } finally {
            setLoading(false);
        }
    }, [selectedMonths, prescribedMonths, orderedMonths]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(dropdownRefs).forEach(key => {
                if (dropdownRefs[key].current && !dropdownRefs[key].current.contains(event.target)) {
                    setShowMonthDropdown(prev => ({ ...prev, [key]: false }));
                }
            });
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRefs]);

    const getAvailableMonths = () => {
        if (!accountCreatedAt) return [];
        const start = moment(accountCreatedAt);
        const now = moment();
        const limit = now.clone().subtract(5, "months").startOf("month");
        const effectiveStart = moment.max(start, limit);

        const months = [];
        let current = effectiveStart.clone().startOf("month");
        while (current.isBefore(now) || current.isSame(now, 'month')) {
            months.push(current.format("MMMM YYYY"));
            current.add(1, "month");
        }
        return months.reverse();
    };

    const availableMonths = getAvailableMonths();

    const toggleMonth = (month, type) => {
        if (type === 'patient') {
            setSelectedMonths(prev =>
                prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
            );
        } else if (type === 'prescribed') {
            setPrescribedMonths(prev =>
                prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
            );
        } else if (type === 'ordered') {
            setOrderedMonths(prev =>
                prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
            );
        }
    };

    return (
        <div className="w-full h-full mx-auto p-4 md:p-8 flex flex-col gap-10">
            {/* 1. Patient Engagement */}
            <section tabIndex="-1">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Patient Engagement</h2>
                        <p className="text-gray-500 mt-2 text-sm md:text-base max-w-3xl">Track clinical throughput and document performance</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 md:gap-4 lg:justify-end">
                        <button
                            onClick={() => setIsCustomAnalysisOpen(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-primary-700 rounded-[12px] min-w-[200px] md:min-w-[260px] justify-center text-[13px] font-bold text-primary-700 hover:text-primary-600 hover:bg-primary-50 hover:border-primary-600 transition-all shadow-sm group active:scale-[0.98]"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="opacity-70 group-hover:opacity-100">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            <span>Product Analysis</span>
                        </button>
                        <MonthDropdown
                            selected={selectedMonths}
                            dropdownKey="patient"
                            dropdownRefs={dropdownRefs}
                            showMonthDropdown={showMonthDropdown}
                            setShowMonthDropdown={setShowMonthDropdown}
                            availableMonths={availableMonths}
                            toggleMonth={toggleMonth}
                        />
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                    {data ? (
                        <>
                            <StatCard title="Prescriptions Generated" icon={Info} counts={data.patientAnalytics.prescriptions} selectedMonths={selectedMonths} />
                            <StatCard title="Orders Fulfilled" icon={CreateOrder} counts={data.patientAnalytics.orders} selectedMonths={selectedMonths} />
                            <StatCard title="Receipts Created" icon={Receipt} counts={data.patientAnalytics.receipts} selectedMonths={selectedMonths} />
                        </>
                    ) : (
                        Array(3).fill(0).map((_, i) => (
                            <div key={i} className="flex-1 h-[220px] bg-white border border-primary-50 rounded-[28px] animate-pulse" />
                        ))
                    )}
                </div>
            </section>

            {/* 2. Top Prescribed Pattern */}
            <section className="bg-gray-50/50 rounded-[32px] p-8 border border-primary-50">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Top Prescribed Products</h2>
                        <p className="text-gray-600 text-sm md:text-base max-w-3xl">Insights into your clinical prescribing patterns</p>
                    </div>
                    <MonthDropdown
                        selected={prescribedMonths}
                        dropdownKey="prescribed"
                        dropdownRefs={dropdownRefs}
                        showMonthDropdown={showMonthDropdown}
                        setShowMonthDropdown={setShowMonthDropdown}
                        availableMonths={availableMonths}
                        toggleMonth={toggleMonth}
                    />
                </div>

                <div className="flex overflow-x-auto gap-6 pb-6 pt-2 scrollbar-hide">
                    {data?.topPrescribedProducts?.map((prod, idx) => (
                        <ProductTile key={idx} product={prod} type="prescribed" />
                    ))}
                    {data?.topPrescribedProducts?.length === 0 && !loading && (
                        <div className="w-full py-12 flex flex-col items-center justify-center text-gray-400 bg-white rounded-[20px] border border-dashed border-gray-200">
                            <Info width={32} height={32} />
                            <p className="mt-2 font-bold">No prescription data for selected months</p>
                        </div>
                    )}
                    {loading && Array(4).fill(0).map((_, i) => (
                        <div key={i} className="min-w-[280px] h-[180px] bg-white border border-primary-50 rounded-[20px] animate-pulse" />
                    ))}
                </div>
            </section>

            {/* 3. Top Ordered Pattern */}
            <section className="bg-gray-50/50 rounded-[32px] p-8 border border-primary-50">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Top Ordered Products</h2>
                        <p className="text-gray-600 text-sm md:text-base max-w-3xl">Performance monitoring by actual product quantities</p>
                    </div>
                    <MonthDropdown
                        selected={orderedMonths}
                        dropdownKey="ordered"
                        dropdownRefs={dropdownRefs}
                        showMonthDropdown={showMonthDropdown}
                        setShowMonthDropdown={setShowMonthDropdown}
                        availableMonths={availableMonths}
                        toggleMonth={toggleMonth}
                    />
                </div>

                <div className="flex overflow-x-auto gap-6 pb-6 pt-2 scrollbar-hide">
                    {data?.topOrderedProducts?.map((prod, idx) => (
                        <ProductTile key={idx} product={prod} type="ordered" />
                    ))}
                    {data?.topOrderedProducts?.length === 0 && !loading && (
                        <div className="w-full py-12 flex flex-col items-center justify-center text-gray-400 bg-white rounded-[20px] border border-dashed border-gray-200">
                            <Info width={32} height={32} />
                            <p className="mt-2 font-bold">No order data for selected months</p>
                        </div>
                    )}
                    {loading && Array(4).fill(0).map((_, i) => (
                        <div key={i} className="min-w-[280px] h-[180px] bg-white border border-primary-50 rounded-[20px] animate-pulse" />
                    ))}
                </div>
            </section>

            <CustomAnalysisModal
                isOpen={isCustomAnalysisOpen}
                onClose={() => setIsCustomAnalysisOpen(false)}
                availableMonths={availableMonths}
                doctorId={doctorId}
                idStore={idStore}
                SID={SID}
            />
        </div>
    );
};

export default Analytics;
